/**
 * Secure API client for Suno Gateway
 *
 * Handles authentication, error handling, and request/response processing.
 * API key is stored in memory only, never persisted to localStorage.
 */

import type {
  GenerateRequest,
  GenerateResponse,
  StatusResponse,
  QuotaResponse,
  HealthResponse,
  ApiError,
} from "../types/api.js";

const API_TIMEOUT_MS = 30_000;

export class ApiClient {
  private baseUrl: string;
  private apiKey: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  setApiKey(key: string): void {
    this.apiKey = key;
  }

  clearApiKey(): void {
    this.apiKey = null;
  }

  hasApiKey(): boolean {
    return this.apiKey !== null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (this.apiKey) {
      headers["Authorization"] = `Bearer ${this.apiKey}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        const error = data as ApiError;
        throw new ApiClientError(
          error.error || "Request failed",
          error.code || "UNKNOWN_ERROR",
          response.status
        );
      }

      return data as T;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err instanceof ApiClientError) {
        throw err;
      }

      if (err instanceof Error && err.name === "AbortError") {
        throw new ApiClientError("Request timed out", "TIMEOUT", 408);
      }

      throw new ApiClientError(
        err instanceof Error ? err.message : "Network error",
        "NETWORK_ERROR",
        0
      );
    }
  }

  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>("/api/v1/health");
  }

  async quota(): Promise<QuotaResponse> {
    return this.request<QuotaResponse>("/api/v1/quota");
  }

  async generate(req: GenerateRequest): Promise<GenerateResponse> {
    return this.request<GenerateResponse>("/api/v1/generate", {
      method: "POST",
      body: JSON.stringify(req),
    });
  }

  async status(taskId: string): Promise<StatusResponse> {
    const sanitizedId = encodeURIComponent(taskId);
    return this.request<StatusResponse>(`/api/v1/status/${sanitizedId}`);
  }

  async download(taskId: string): Promise<Blob> {
    const sanitizedId = encodeURIComponent(taskId);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60_000);

    const headers: Record<string, string> = {};
    if (this.apiKey) {
      headers["Authorization"] = `Bearer ${this.apiKey}`;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/download/${sanitizedId}`,
        { headers, signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const data = await response.json();
        throw new ApiClientError(
          data.error || "Download failed",
          data.code || "DOWNLOAD_ERROR",
          response.status
        );
      }

      return response.blob();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof ApiClientError) throw err;
      throw new ApiClientError("Download failed", "NETWORK_ERROR", 0);
    }
  }
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}
